import React, { useEffect, useState } from 'react';
import { Form, Input, Button, Steps, message, Typography, Modal } from 'antd';
import { MobileOutlined, SafetyOutlined, LockOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { sendVerificationCode, resetPassword } from '../../services/auth';
import authService from '../../services/auth';
import tianaiCaptcha from '../../services/captcha';
import { ErrorHandler, FormValidator } from '../../utils/errorHandler';
import './ForgotPassword.css';

const { Step } = Steps;
const { Title, Text } = Typography;

/**
 * 忘记密码
 * 职责：引导用户通过手机验证码重置密码，三步流程（验证手机号 → 验证验证码 → 设置新密码）。
 * 依赖：`sendVerificationCode`、`resetPassword` 服务；`ErrorHandler`、`FormValidator` 工具处理错误与校验。
 * 状态：`current` 步骤进度；`loading` 请求状态；`countdown` 重发倒计时；`phone` 与 `verificationCode` 当前上下文。
 * 交互：表单校验、验证码发送与重发、步骤切换、重置成功后延时跳转登录页。
 */
const ForgotPasswordPage = () => {
  const [current, setCurrent] = useState(0);
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [phone, setPhone] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [smsSendInterval, setSmsSendInterval] = useState(60); // 默认60秒

  // 验证码相关状态
  const [captchaVisible, setCaptchaVisible] = useState(false);
  const [pendingPhone, setPendingPhone] = useState('');
  const [pendingPurpose, setPendingPurpose] = useState('');

  // 倒计时效果
  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  // 获取短信验证码配置（发送间隔等）
  useEffect(() => {
    authService.getCaptchaConfig()
      .then(res => {
        if (res?.data?.smsSendInterval) {
          setSmsSendInterval(res.data.smsSendInterval);
        }
      })
      .catch(err => {
        console.error('获取验证码配置失败:', err);
        // 使用默认值60秒
      });
  }, []);

  // 发送验证码：校验手机号，通过服务触发短信发送并开始倒计时，进入下一步
  const handleSendCode = async (values) => {
    setLoading(true);
    try {
      await doSendCode(values.phone);
    } catch (error) {
      ErrorHandler.handleVerificationError(error, 'send');
    } finally {
      setLoading(false);
    }
  };

  // 验证验证码：此处演示直接进入下一步，实际可调用 `verifyCode` 做服务端校验
  const handleVerifyCode = async (values) => {
    setVerificationCode(values.code);
    setCurrent(2);
  };

  // 重置密码：提交新密码与验证码，成功后提示并跳转登录
  const handleResetPassword = async (values) => {
    setLoading(true);
    try {
      await resetPassword({
        phone,
        code: verificationCode,
        newPassword: values.newPassword
      });
      message.success('密码重置成功，请使用新密码登录');
      setTimeout(() => {
        navigate('/auth/login');
      }, 2000);
    } catch (error) {
      ErrorHandler.handleAuthError(error, 'reset');
    } finally {
      setLoading(false);
    }
  };

  // 重新发送验证码：基于当前 `phone` 再次触发发送与倒计时
  const handleResendCode = async () => {
    setLoading(true);
    try {
      await doSendCode(phone);
    } catch (error) {
      ErrorHandler.handleVerificationError(error, 'send');
    } finally {
      setLoading(false);
    }
  };

  // 实际发送验证码逻辑
  const doSendCode = async (phoneNumber) => {
    const res = await sendVerificationCode(phoneNumber, 'reset-password');
    const data = res?.data;

    if (data?.captchaRequired && !data?.sent) {
      // 需要完成人机验证
      setPendingPhone(phoneNumber);
      setPendingPurpose('reset-password');
      setCaptchaVisible(true);
      message.warning(res?.message || '需要完成人机验证');
      return;
    }

    // 直接发送成功
    setPhone(phoneNumber);
    setCountdown(smsSendInterval);
    setCurrent(1);
  };

  // 处理验证码验证成功
  const handleCaptchaSuccess = async (token) => {
    try {
      // 使用验证码token发送短信验证码
      const res = await sendVerificationCode(pendingPhone, pendingPurpose, token);
      const responseData = res?.data;

      if (responseData?.sent) {
        message.success('验证码已发送到您的手机');
        setPhone(pendingPhone);
        setCountdown(smsSendInterval);
        setCurrent(1);
        setCaptchaVisible(false);
      } else {
        message.error(responseData?.message || '发送验证码失败');
      }
    } catch (error) {
      message.error(error?.message || '发送验证码失败');
    }
  };

  // 返回上一步
  const handlePrevious = () => {
    setCurrent(current - 1);
  };

  // 返回登录页
  const handleBackToLogin = () => {
    navigate('/auth/login');
  };

  // 当验证码弹窗打开时，初始化验证码
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!captchaVisible) return;

    let disposed = false;
    const timer = setTimeout(() => {
      if (disposed) return;
      tianaiCaptcha.show({
        bindEl: '#tianai-captcha-box',
        onValidSuccess: (token, tac) => {
          handleCaptchaSuccess(token);
          tac.destroyWindow();
          setCaptchaVisible(false);
        },
        onValidFail: () => {
          message.error('验证失败，请重试');
        }
      }).catch((error) => {
        console.error('验证码初始化失败:', error);
        message.error(error?.message || '验证码初始化失败');
        setCaptchaVisible(false);
      });
    }, 0);

    return () => {
      disposed = true;
      clearTimeout(timer);
      tianaiCaptcha.destroy();
    };
  }, [captchaVisible]);

  const steps = [
    {
      title: '验证手机号',
      icon: <MobileOutlined />,
      content: (
        <Form
          form={form}
          name="phone-verification"
          onFinish={handleSendCode}
          layout="vertical"
          size="large"
        >
          <Form.Item
            name="phone"
            label="手机号码"
            rules={[
              { required: true, message: '请输入手机号码' },
              { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号码' }
            ]}
          >
            <Input
              prefix={<MobileOutlined />}
              placeholder="请输入注册时使用的手机号码"
              maxLength={11}
            />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              block
              size="large"
            >
              发送验证码
            </Button>
          </Form.Item>
        </Form>
      )
    },
    {
      title: '验证验证码',
      icon: <SafetyOutlined />,
      content: (
        <Form
          name="code-verification"
          onFinish={handleVerifyCode}
          layout="vertical"
          size="large"
        >
          <div className="phone-info">
            <Text type="secondary">
              验证码已发送至 {phone?.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2')}
            </Text>
          </div>
          <Form.Item
            name="code"
            label="验证码"
            rules={[
              { required: true, message: '请输入验证码' },
              { len: 6, message: '验证码为6位数字' }
            ]}
          >
            <Input
              prefix={<SafetyOutlined />}
              placeholder="请输入6位验证码"
              maxLength={6}
            />
          </Form.Item>
          <div className="resend-section">
            {countdown > 0 ? (
              <Text type="secondary">{countdown}秒后可重新发送</Text>
            ) : (
              <Button type="link" onClick={handleResendCode} loading={loading}>
                重新发送验证码
              </Button>
            )}
          </div>
          <Form.Item>
            <div className="button-group">
              <Button onClick={handlePrevious} size="large">
                上一步
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                size="large"
              >
                验证
              </Button>
            </div>
          </Form.Item>
        </Form>
      )
    },
    {
      title: '重置密码',
      icon: <LockOutlined />,
      content: (
        <Form
          name="reset-password"
          onFinish={handleResetPassword}
          layout="vertical"
          size="large"
        >
          <Form.Item
            name="newPassword"
            label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 6, message: '密码至少6位字符' },
              { max: 20, message: '密码最多20位字符' }
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="请输入新密码（6-20位字符）"
            />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label="确认密码"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: '请确认新密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  return FormValidator.validatePasswordConfirm(getFieldValue('newPassword'), value);
                }
              })
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="请再次输入新密码"
            />
          </Form.Item>
          <Form.Item>
            <div className="button-group">
              <Button onClick={handlePrevious} size="large">
                上一步
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                size="large"
              >
                重置密码
              </Button>
            </div>
          </Form.Item>
        </Form>
      )
    }
  ];

  return (
    <div className="forgot-password-page">
      <div className="forgot-password-header">
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={handleBackToLogin}
          className="back-button"
        >
          返回登录
        </Button>
        <Title level={3} className="forgot-password-title">
          忘记密码
        </Title>
      </div>

      <Steps current={current} className="forgot-password-steps">
        {steps.map((item, index) => (
          <Step key={index} title={item.title} icon={item.icon} />
        ))}
      </Steps>

      <div className="forgot-password-content">
        {steps[current].content}
      </div>

      {/* 验证码弹窗 */}
      <Modal
        open={captchaVisible}
        title="安全验证"
        onCancel={() => setCaptchaVisible(false)}
        footer={null}
        destroyOnClose
        centered
      >
        <div id="tianai-captcha-box" style={{ minWidth: 318, minHeight: 318 }} />
      </Modal>
    </div>
  );
};

export default ForgotPasswordPage;
